import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { OverrideOutcomeRecord } from "@/data/contractor/networkExecution/overrideRereview";

// ── DB row shape — matches override_outcome_records table ─────────────
// Composite PK: (observation_id, recorded_at) — no synthetic id column.
interface OverrideOutcomeRecordRow {
  observation_id: string;
  job_id: string;
  outcome: string;
  reviewer_id: string;
  reviewer_role: string;
  reason: string;
  recorded_at: string;
  enforcement_mode_at_write: string;
  organization_id: string;
}

// ── Mappers ───────────────────────────────────────────────────────────
function toUiRecord(row: OverrideOutcomeRecordRow): OverrideOutcomeRecord {
  return {
    observationId: row.observation_id,
    jobId: row.job_id,
    outcome: row.outcome as OverrideOutcomeRecord["outcome"],
    reviewerId: row.reviewer_id,
    reviewerRole: row.reviewer_role,
    reason: row.reason,
    recordedAt: row.recorded_at,
    enforcementModeAtWrite: row.enforcement_mode_at_write as OverrideOutcomeRecord["enforcementModeAtWrite"],
  };
}

function toDbRow(
  input: OverrideOutcomeRecord,
  organizationId: string | null,
): OverrideOutcomeRecordRow {
  return {
    observation_id: input.observationId,
    job_id: input.jobId,
    outcome: input.outcome,
    reviewer_id: input.reviewerId,
    reviewer_role: input.reviewerRole,
    reason: input.reason,
    recorded_at: input.recordedAt,
    enforcement_mode_at_write: input.enforcementModeAtWrite,
    organization_id: organizationId as string,
  };
}

// ── Service ───────────────────────────────────────────────────────────
export const OverrideOutcomeService = {
  async getAll(): Promise<{ data: OverrideOutcomeRecord[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("override_outcome_records")
      .select("*")
      .order("recorded_at", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as OverrideOutcomeRecordRow[]).map(toUiRecord), error: null };
  },

  async getByObservationId(observationId: string): Promise<{ data: OverrideOutcomeRecord[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("override_outcome_records")
      .select("*")
      .eq("observation_id", observationId)
      .order("recorded_at", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as OverrideOutcomeRecordRow[]).map(toUiRecord), error: null };
  },

  async record(input: OverrideOutcomeRecord): Promise<{ data: OverrideOutcomeRecord | null; error: unknown }> {
    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const row = toDbRow(input, organizationId);

    const { data, error } = await supabase
      .from("override_outcome_records")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[OverrideOutcomeService.record] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiRecord(data as OverrideOutcomeRecordRow), error: null };
  },
};
